import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import CountUp from "react-countup";

import { lighten } from "polished";
import { Tooltip as ReactTooltip } from "react-tooltip";
import isEmpty from "lodash/isEmpty";

import { generateUniqueId } from "../../utils";

const CallBars = styled.div`
  width: 100%;
  height: 20px;
  display: flex;
  overflow: hidden;
  background: hsla(200, 50%, 90%, 1);

  .progress {
    transition: width 2s ease-in-out;
    border: 1px solid
      ${(props) => (props.theme.nightmode ? props.theme.dark_blue : "white")} !important;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    cursor: pointer;
    border-radius: 0 !important;
    padding-left: 5px;
    &.success {
      background: ${(props) => props.theme.green_g};
      &:hover {
        background: ${(props) => lighten(0.03, props.theme.green_g)};
      }
      p {
        color: #fff !important;
      }
    }
    &.warning {
      background: ${(props) => props.theme.gold_g};
      &:hover {
        background: ${(props) => lighten(0.03, props.theme.gold_g)};
      }
    }
    &.danger {
      background: ${(props) => props.theme.red_g};
      &:hover {
        background: ${(props) => lighten(0.03, props.theme.red_g)};
      }
      p {
        color: #fff !important;
      }
    }
    &:hover {
      opacity: 0.9;
    }
    p {
      font-family: "Nunito";
      text-transform: uppercase;
      font-size: 1.2rem;
    }
  }
`;

const getPercentageStyles = (total, budget) => {
  const percentage = parseFloat(total / budget) * 100;
  if (percentage < 70) {
    return "success";
  }
  if (percentage >= 71 && percentage < 100) {
    return "warning";
  }
  return "danger";
};

const ProjectCallBreakdown = ({ project, auth, calls }) => {
  if (calls.length && !isEmpty(project)) {
    const totalHours = calls.reduce(
      (total, call) => total + call.totalHours,
      0
    );
    const totalBillableHours = calls.reduce(
      (total, call) => total + call.totalBillableHours,
      0
    );

    const budgetHours = project.budget_hours;

    const className = getPercentageStyles(totalHours, budgetHours);

    const metrics = [
      {
        label: "Total Actual Hours",
        end: totalHours || 0,
        suffix: " hrs"
      },
      {
        label: "Total Billable Hours",
        end: totalBillableHours || 0,
        suffix: " hrs"
      }
    ];

    return (
      <React.Fragment>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            justifyItems: "start",
            alignContent: "center",
            textAlign: "center",
            gap: "15px",
            marginBottom: "15px"
          }}
        >
          <div />
          {metrics.map((metric, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent: "space-evenly",
                alignItems: "center",
                width: "100%"
              }}
            >
              <p style={{ marginBottom: 0 }}>{metric.label}</p>
              <CountUp
                className="counter"
                suffix={metric.suffix}
                prefix={metric.prefix}
                decimals={2}
                separator=","
                end={metric.end}
                style={{ color: "#50accb" }}
              />
            </div>
          ))}
        </div>
        <CallBars>
          {calls.map((group, i) => {
            const percent =
              totalHours === 0 ? 0 : (group.totalHours / totalHours) * 100;
            const assignedTo = group.userInfo
              ? group.userInfo.email.split("@")[0].toUpperCase()
              : "Unassigned";

            return (
              <React.Fragment key={generateUniqueId()}>
                <div
                  key={assignedTo}
                  className={`animated slideInLeft progress ${className}`}
                  style={{ width: `${percent}%` }}
                  id={assignedTo}
                  data-tooltip-variant={auth.isNightMode ? "dark" : "light"}
                  data-tooltip-content={`${assignedTo} | ${group.totalHours} hours`}
                >
                  <p>
                    {assignedTo} {percent.toFixed(0)}%
                  </p>
                </div>
                <ReactTooltip
                  anchorId={assignedTo}
                  className={`custom-tooltip ${
                    auth.isNightMode ? "dark" : "light"
                  }`}
                  key={i}
                  place="bottom"
                  type="light"
                  effect="solid"
                />
              </React.Fragment>
            );
          })}
        </CallBars>
      </React.Fragment>
    );
  }
  return null;
};

ProjectCallBreakdown.propTypes = {
  project: PropTypes.object,
  calls: PropTypes.array,
  auth: PropTypes.object
};

export default ProjectCallBreakdown;
